import { RightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { GetActivityListBody } from '@knowledge/service/api/activity';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { Checkbox, message, Select } from 'antd';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { useEffect, useState } from 'react';
import { getRegionsStoreCount, RegionCountsMap } from 'services/api/admin';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import { useGetRegionLabel } from 'hooks/business/useRegion';

interface PlatformRegion {
  platform: General.PlatformUpperCase;
  regionList: General.UpperCaseRegion[];
}

function PlatformRegionSelector({
  value,
  onChange,
}: {
  value: GetActivityListBody['platformStoreScope'];
  onChange: (value: GetActivityListBody['platformStoreScope']) => void;
}) {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { loading, data = [] } = useRequest(
    () => {
      return getRegionsStoreCount(permissionNameIdMap.查看活动数据).then((res) => {
        const defaultEmpty = {} as RegionCountsMap;
        const {
          shopeeCountData,
          lazadaCountData,
          tokopediaCountData = defaultEmpty,
          tiktokCountData,
          tiktokUsCountData = defaultEmpty,
          aliExpressCountData = defaultEmpty,
          mercadoCountData = defaultEmpty,
          kwaiCountData = defaultEmpty,
          facebookCountData = defaultEmpty,
        } = res.data.data;
        const getRegionList = (regionCountsMap: RegionCountsMap) => {
          return Object.keys(regionCountsMap).map((region) => {
            return toUpperCase(region.replace(/^(\w{2,})Cnt$/, '$1')) as General.UpperCaseRegion;
          });
        };
        return [
          {
            platform: 'SHOPEE',
            regionList: getRegionList(shopeeCountData),
          },
          {
            platform: 'LAZADA',
            regionList: getRegionList(lazadaCountData),
          },
          {
            platform: 'TOKOPEDIA',
            regionList: getRegionList(tokopediaCountData),
          },
          {
            platform: 'TIKTOK',
            regionList: getRegionList(tiktokCountData),
          },
          {
            platform: 'TIKTOK_US',
            regionList: getRegionList(tiktokUsCountData),
          },
          {
            platform: 'ALIEXPRESS',
            regionList: getRegionList(aliExpressCountData),
          },
          {
            platform: 'MERCADO',
            regionList: getRegionList(mercadoCountData),
          },
          {
            platform: 'KWAI',
            regionList: getRegionList(kwaiCountData),
          },
          {
            platform: 'FACEBOOK',
            regionList: getRegionList(facebookCountData),
          },
        ].filter(({ regionList }) => regionList.length) as PlatformRegion[];
      });
    },
    {
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const [activePlatform, setActivePlatform] = useState<PlatformRegion>();

  useEffect(() => {
    if (data?.length) {
      setActivePlatform(data[0]);
    }
  }, [data]);

  const onSelect = (
    checked: boolean,
    platform: General.PlatformUpperCase,
    region: General.UpperCaseRegion,
  ) => {
    const target = value?.find((_) => _.platform === platform);
    if (!target) {
      onChange([...(value || []), { platform: platform, regionStoreScopes: [{ region }] }]);
    } else {
      const nextTarget = {
        ...target,
        regionStoreScopes: checked
          ? [...target.regionStoreScopes, { region }]
          : target.regionStoreScopes.filter((_) => _.region !== region),
      };
      onChange([
        ...(value?.filter((_) => _.platform !== platform) || []),
        ...(nextTarget.regionStoreScopes.length ? [nextTarget] : []),
      ]);
    }
  };

  return (
    <Select
      maxTagCount="responsive"
      loading={loading}
      dropdownMatchSelectWidth={false}
      mode="multiple"
      placeholder={t('选择平台站点')}
      value={value?.reduce<{ value: string; label: string }[]>(
        (res, { platform, regionStoreScopes }) => {
          return regionStoreScopes.reduce((res, { region }) => {
            res.push({
              value: `${platform}@${region}`,
              label: `${platformLabelMap[toLowerCase(platform)]} - ${getRegionLabel(region)}`,
            });
            return res;
          }, res);
        },
        [],
      )}
      onDeselect={(value: any) => {
        const [platform, region] = value.split('@');
        onSelect(false, platform as any, region as any);
      }}
      dropdownRender={() =>
        data ? (
          <Container>
            <ul>
              {data.map((config) => {
                const { platform, regionList } = config;
                const target = value?.find((_) => _.platform === platform);
                const checked = regionList.every((region) =>
                  target?.regionStoreScopes.some((_) => _.region === region),
                );
                const indeterminate =
                  !checked &&
                  regionList.some((region) =>
                    target?.regionStoreScopes.some((_) => _.region === region),
                  );
                return (
                  <li
                    className={activePlatform?.platform === platform ? activeItem : undefined}
                    onClick={() => setActivePlatform(config)}
                  >
                    <div>
                      <Checkbox
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={({ target: { checked } }) =>
                          onChange([
                            ...(value?.filter((_) => _.platform !== platform) || []),
                            ...(checked
                              ? [
                                  {
                                    platform,
                                    regionStoreScopes: regionList.map((region) => ({
                                      region,
                                    })),
                                  },
                                ]
                              : []),
                          ])
                        }
                      />
                      {platformLabelMap[toLowerCase(platform)]}
                    </div>
                    <RightOutlined />
                  </li>
                );
              })}
            </ul>
            {activePlatform && (
              <ul>
                {activePlatform.regionList.map((region) => {
                  const checked = value?.some(
                    (_) =>
                      _.platform === activePlatform.platform &&
                      _.regionStoreScopes.some((_) => _.region === region),
                  );
                  return (
                    <li className={checked ? activeItem : undefined}>
                      <div>
                        <Checkbox
                          checked={checked}
                          onChange={({ target: { checked } }) => {
                            onSelect(checked, activePlatform.platform, region);
                          }}
                        />
                        {getRegionLabel(region)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Container>
        ) : (
          <></>
        )
      }
    />
  );
}

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  border-radius: 8px;
  > ul {
    width: 220px;
    margin: 0;
    padding: 4px;
    list-style: none;
    > li {
      display: flex;
      align-items: center;
      height: 32px;
      padding: 0 12px;
      border-radius: 6px;
      color: var(--fett-text-primary);
      cursor: pointer;
      > div {
        flex: 1 0 0;
        > label {
          margin-right: 4px;
        }
      }
      .anticon {
        color: var(--fett-text-tertiary);
      }
    }
    &:first-of-type {
      border-right: solid 1px var(--fett-border-default);
    }
    &:not(:first-of-type) {
      margin-left: -1px;
      border-left: solid 1px var(--fett-border-default);
    }
  }
`;

/** 下拉选中项浅蓝底，暗色下换为品牌蓝透明底 */
const activeItem = css`
  background: #e6f4ff;
  [data-color-mode='dark'] & {
    background: rgba(51, 102, 255, 0.16);
  }
`;

export default PlatformRegionSelector;
